import { useState, useMemo, useEffect } from "react";

import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  LinkedinOutlined,
  LinkOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import { App } from "antd";
import { Helmet } from "react-helmet-async";
import _ from "lodash";
import { DateTime } from "luxon";

import {
  useDeleteSurveyViewMutation,
  useGetCurrentUserQuery,
  useGetSurveyViewByIdQuery,
  useLazyGetSurveyByIdQuery,
  useViewSurveyViewQuery,
} from "@/redux/service";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useSurveyViewDownload } from "@/views/survey-views/hooks/useSurveyViewDownload";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { RootState } from "@/redux/store";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import SurveyIcon from "@/assets/icons/survey-icon.svg?react";
import CommunityIcon from "@/assets/icons/community.svg?react";
import CalendarIcon from "@/assets/icons/calendar.svg?react";
import BaseDivider from "@/components_v2/Divider/BaseDivider";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import { OrganizationRoles } from "@/utilities/constants";
import { SurveyViewPublicationDestination } from "@/redux/survey-views/types";

const SurveyView = () => {
  const { surveyViewUuid } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notification } = App.useApp();

  const [zoomLevel, setZoomLevel] = useState(100);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated,
  );
  const { downloadSurveyView, isDownloading } = useSurveyViewDownload();
  const { data: previewData } = useViewSurveyViewQuery(
    surveyViewUuid as string,
  );
  const { data: surveyViewData } = useGetSurveyViewByIdQuery(
    surveyViewUuid as string,
  );
  const { data: currentUser } = useGetCurrentUserQuery();
  const [getSurvey, surveyResponse] = useLazyGetSurveyByIdQuery();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteSurveyView] = useDeleteSurveyViewMutation();

  useEffect(() => {
    if (surveyViewData) {
      getSurvey({ surveyId: surveyViewData.survey });
    }
  }, [surveyViewData]);

  const handleZoom = (direction: "in" | "out") => {
    setZoomLevel((prev) => {
      const newZoom = direction === "in" ? prev + 25 : prev - 25;
      return Math.max(50, Math.min(200, newZoom));
    });
  };

  const handleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Fullscreen error:", error);
    }
  };

  const handleDeleteSurveyView = async (surveyViewId: string) => {
    deleteSurveyView(surveyViewId)
      .unwrap()
      .then(() => {
        notification.error({
          message: t("surveyViews.labels.surveyViewDeleted"),
        });
      });
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const modifiedHtmlData = useMemo(() => {
    if (!previewData?.htmlData) return "";
    return previewData.htmlData.replace(
      "<html",
      `<html style="transform: scale(${zoomLevel / 100}); transform-origin: top left; display: flex; -webkit-transform-origin-x: left;"`,
    );
  }, [previewData?.htmlData, zoomLevel]);

  const canEditSurveyView = useMemo(() => {
    if (!surveyViewData || !currentUser) return false;
    return (
      currentUser.id === surveyViewData.createdBy.id ||
      currentUser.organizationRole === OrganizationRoles.ADMIN.value
    );
  }, [currentUser, surveyViewData]);

  const canDeleteSurveyView = useMemo(() => {
    if (!surveyViewData || !currentUser) return false;
    return (
      surveyViewData.config.publicationDestination !==
        SurveyViewPublicationDestination.WEBSITE &&
      (currentUser.id === surveyViewData.createdBy.id ||
        currentUser.organizationRole === OrganizationRoles.ADMIN.value)
    );
  }, [currentUser, surveyViewData]);

  const copyToClipboard = (surveyViewId: string) => {
    navigator.clipboard
      .writeText(
        `${window.location.origin}/public/reports/views/${surveyViewId}`,
      )
      .then(() => {
        notification.success({ message: t("shareLinkCopied") });
      });
  };

  const handleLinkedInSharing = () => {
    window.open(
      `https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}`,
      "_blank",
    );
  };

  return (
    <>
      {surveyViewData && (
        <Helmet>
          <title>{`PRME Commons - ${surveyViewData.title}`}</title>
          <meta
            name="og:title"
            content={`PRME Commons - ${surveyViewData.title}`}
          />
          <meta name="og:url" content={window.location.href} />
          <meta name="og:article" content="article" />
          <meta
            name="og:description"
            content={_.truncate(surveyViewData.description)}
          />
        </Helmet>
      )}
      <div className="survey-view-container">
        <div className="survey-view-container-content">
          {isAuthenticated && (
            <BaseButton
              size="small"
              variant="tertiary"
              onClick={() => navigate(-1)}
            >
              {t("datablocks.labels.back")}
            </BaseButton>
          )}

          <div className="survey-view-header">
            <h1 className="header-l-medium">
              {t("surveyViews.labels.downloadAndShare")}
            </h1>

            <div className="survey-view-header-buttons">
              <DropdownMenu
                placement="bottom"
                content={
                  <div>
                    {(canEditSurveyView || canDeleteSurveyView) && (
                      <>
                        <h6 className="dropdown-menu-title body-s-medium">
                          {t("surveyViews.labels.actions")}
                        </h6>

                        {canEditSurveyView && (
                          <BaseButton
                            isFullWidth
                            className="dropdown-menu-button"
                            variant="link"
                            leadingIcon={<EditOutlined />}
                            onClick={() =>
                              navigate(`/reports/views/${surveyViewUuid}/form`)
                            }
                          >
                            {t("buttons.editSurveyView")}
                          </BaseButton>
                        )}

                        {canDeleteSurveyView && (
                          <BaseButton
                            isFullWidth
                            className="dropdown-menu-button"
                            variant="link"
                            leadingIcon={<DeleteOutlined />}
                            onClick={() => {
                              setOpenDeleteModal(true);
                            }}
                          >
                            {t("buttons.deleteSurveyView")}
                          </BaseButton>
                        )}
                        <BaseDivider />
                      </>
                    )}

                    <h6 className="dropdown-menu-title body-s-medium">
                      {t("surveyViews.labels.shareOptions")}
                    </h6>
                    {surveyViewUuid && (
                      <BaseButton
                        isFullWidth
                        className="dropdown-menu-button"
                        variant="link"
                        leadingIcon={<LinkOutlined />}
                        onClick={() => copyToClipboard(surveyViewUuid)}
                      >
                        {t("surveyViews.labels.copyLink")}
                      </BaseButton>
                    )}

                    <BaseButton
                      isFullWidth
                      className="dropdown-menu-button"
                      variant="link"
                      leadingIcon={<LinkedinOutlined />}
                      onClick={() => handleLinkedInSharing()}
                    >
                      {t("surveyViews.labels.shareOnLinkedIn")}
                    </BaseButton>
                  </div>
                }
              >
                <BaseButton variant="secondary">•••</BaseButton>
              </DropdownMenu>

              {surveyViewUuid && (
                <BaseButton
                  leadingIcon={
                    isDownloading(surveyViewUuid) ? (
                      <BaseSpinner size="small" />
                    ) : (
                      <DownloadOutlined />
                    )
                  }
                  onClick={() => downloadSurveyView(surveyViewUuid)}
                  disabled={isDownloading(surveyViewUuid)}
                >
                  {isDownloading(surveyViewUuid)
                    ? t("surveyViews.labels.generating")
                    : t("surveyViews.labels.downloadAsPdf")}
                </BaseButton>
              )}
            </div>
          </div>

          <div className="survey-view-main-content">
            <div className="survey-view-info-panel">
              <div className="survey-view-info-section">
                <h2 className="survey-view-info-title header-m-medium">
                  {t("surveyViews.labels.surveyViewDetails")}
                </h2>
                {surveyResponse.isSuccess && (
                  <h2 className="survey-view-info-title header-s-medium">
                    {surveyResponse.data.title}
                  </h2>
                )}
                {surveyViewData && (
                  <>
                    <div className="survey-view-info-report-item-container">
                      <BaseSvgRenderer
                        component={SurveyIcon}
                        width="16"
                        height="16"
                      />
                      <h3 className="survey-view-info-report-title body-m-regular">
                        {surveyViewData.title}
                      </h3>
                    </div>

                    {surveyViewData.description && (
                      <div>
                        <div className="survey-view-info-description body-m-regular">
                          {surveyViewData.description}
                        </div>
                      </div>
                    )}

                    <BaseDivider />

                    <div className="survey-view-info-report-item-container">
                      <BaseSvgRenderer
                        component={CommunityIcon}
                        width="16"
                        height="16"
                      />
                      <div className="survey-view-info-organization body-m-regular">
                        {surveyViewData.organization?.name}
                      </div>
                    </div>

                    <div className="survey-view-info-report-item-container">
                      <BaseSvgRenderer
                        component={CalendarIcon}
                        width="16"
                        height="16"
                      />
                      {DateTime.fromISO(surveyViewData.createdAt).toFormat(
                        "dd MMM yyyy",
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="survey-view-preview-section">
              <div className="survey-view-toolbar">
                <div className="survey-view-zoom-controls">
                  <BaseButton
                    size="small"
                    variant="tertiary"
                    onClick={() => handleZoom("out")}
                    disabled={zoomLevel <= 50}
                  >
                    <ZoomOutOutlined />
                  </BaseButton>
                  <span className="survey-view-zoom-level">{zoomLevel}%</span>
                  <BaseButton
                    size="small"
                    variant="tertiary"
                    onClick={() => handleZoom("in")}
                    disabled={zoomLevel >= 200}
                  >
                    <ZoomInOutlined />
                  </BaseButton>
                  <div className="survey-view-controls-separator" />
                  <BaseButton
                    size="small"
                    variant="tertiary"
                    onClick={handleFullscreen}
                  >
                    {isFullscreen ? (
                      <FullscreenExitOutlined />
                    ) : (
                      <FullscreenOutlined />
                    )}
                  </BaseButton>
                </div>
              </div>

              <div
                className={`survey-view-container-content-preview ${isFullscreen ? "fullscreen" : ""}`}
              >
                {previewData && (
                  <iframe
                    srcDoc={modifiedHtmlData}
                    title="Survey Data"
                    style={{
                      borderRadius: "var(--radius-s)",
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <CancelConfirmationModal
          title={t("surveyViews.labels.deleteSurveyView")}
          content={t("surveyViews.labels.deleteSurveyView")}
          confirmButtonText={t("buttons.delete")}
          open={openDeleteModal}
          onCancel={() => setOpenDeleteModal(false)}
          onConfirm={async () => {
            if (surveyViewUuid) {
              await handleDeleteSurveyView(surveyViewUuid);
            }
            setOpenDeleteModal(false);
            navigate("/reports/views");
          }}
        />
      </div>
    </>
  );
};

export default SurveyView;
